// Comment Panel for Document Collaboration
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { MessageSquare, Send, Check, X, Trash2, MoreVertical, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export function CommentPanel({ documentId, isOwner = false }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [lineNumber, setLineNumber] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [resolvingComment, setResolvingComment] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [actionTaken, setActionTaken] = useState('');

  useEffect(() => {
    loadComments();
    // Poll for new comments every 10 seconds
    const interval = setInterval(loadComments, 10000);
    return () => clearInterval(interval);
  }, [documentId, showResolved]);

  const loadComments = async () => {
    try {
      const data = await api.request(`/comments/document/${documentId}?include_resolved=${showResolved}`);
      setComments(data);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    setError('');

    try {
      // Prepend line number to comment if provided
      const commentContent = lineNumber 
        ? `[Line ${lineNumber}] ${newComment}` 
        : newComment;

      await api.request('/comments/', {
        method: 'POST',
        body: JSON.stringify({
          document_id: documentId,
          content: commentContent,
          parent_comment_id: replyTo
        })
      });

      setNewComment('');
      setLineNumber('');
      setReplyTo(null);
      await loadComments();
    } catch (err) {
      setError(err.message || 'Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (commentId, isResolved) => {
    try {
      await api.request(`/comments/${commentId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_resolved: !isResolved })
      });
      await loadComments();
    } catch (err) {
      console.error('Failed to update comment:', err);
    }
  };

  const handleDelete = async (commentId) => {
    if (!confirm('Delete this comment?')) return;

    try {
      await api.request(`/comments/${commentId}`, {
        method: 'DELETE'
      });
      await loadComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  // Group comments into threads
  const threads = comments.filter(c => !c.parent_comment_id);
  const getReplies = (parentId) => comments.filter(c => c.parent_comment_id === parentId);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const CommentItem = ({ comment, isReply = false }) => {
    const isAuthor = user?.id === comment.user_id;
    const replies = getReplies(comment.id);

    return (
      <div className={`${isReply ? 'ml-8 mt-2' : 'mb-4'}`}>
        <Card className={`${comment.is_resolved ? 'opacity-60 bg-gray-50' : ''} ${isReply ? 'border-l-4 border-l-purple-200' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-purple-700">
                      {comment.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{comment.user_name}</div>
                    <div className="text-xs text-gray-500">{formatDate(comment.created_at)}</div>
                  </div>
                  {comment.is_resolved && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Resolved
                    </span>
                  )}
                </div>
                
                {comment.selected_text && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 mb-2 text-sm italic">
                    "{comment.selected_text}"
                  </div>
                )}
                
                <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
              </div>

              <div className="flex gap-1">
                {!comment.is_resolved && (
                  <Button
                    onClick={() => setReplyTo(comment.id)}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    title="Reply"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                )}
                
                {(isOwner || isAuthor) && (
                  <>
                    <Button
                      onClick={() => handleResolve(comment.id, comment.is_resolved)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={comment.is_resolved ? "Unresolve" : "Resolve"}
                    >
                      {comment.is_resolved ? (
                        <X className="h-4 w-4 text-red-500" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </Button>
                    
                    <Button
                      onClick={() => handleDelete(comment.id)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Replies */}
        {replies.length > 0 && (
          <div className="mt-2">
            {replies.map(reply => (
              <CommentItem key={reply.id} comment={reply} isReply={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const stats = {
    total: comments.length,
    resolved: comments.filter(c => c.is_resolved).length,
    unresolved: comments.filter(c => !c.is_resolved).length
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-purple-600" />
            Comments ({stats.unresolved})
          </h3>
          <Button
            onClick={() => setShowResolved(!showResolved)}
            variant="outline"
            size="sm"
          >
            {showResolved ? 'Hide Resolved' : 'Show All'}
          </Button>
        </div>
        
        {stats.total > 0 && (
          <div className="flex gap-4 text-sm text-gray-600">
            <span>{stats.unresolved} open</span>
            <span>{stats.resolved} resolved</span>
          </div>
        )}
      </div>

      {/* Comment List */}
      <div className="flex-1 overflow-y-auto p-4">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {threads.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-semibold mb-2">No comments yet</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-sm mx-auto mt-4">
              <p className="text-xs text-blue-900 font-semibold mb-2">💡 How to leave feedback:</p>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>Check line numbers on the left of the document</li>
                <li>Enter the line # in the field below</li>
                <li>Write your comment</li>
                <li>Click send!</li>
              </ol>
              <p className="text-xs text-blue-700 mt-2 italic">
                Line numbers help the owner know exactly what you're referring to.
              </p>
            </div>
          </div>
        ) : (
          threads.map(comment => (
            <CommentItem key={comment.id} comment={comment} />
          ))
        )}
      </div>

      {/* New Comment Form */}
      <div className="p-4 border-t bg-gray-50 relative z-10">
        {replyTo && (
          <div className="mb-2 flex items-center justify-between bg-blue-50 px-3 py-2 rounded text-sm">
            <span className="text-blue-700">Replying to comment...</span>
            <Button
              onClick={() => setReplyTo(null)}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="flex gap-2 items-center">
            <label className="text-xs text-gray-600 whitespace-nowrap">Line #:</label>
            <Input
              type="number"
              value={lineNumber}
              onChange={(e) => setLineNumber(e.target.value)}
              placeholder="Optional"
              className="w-24 h-8 text-sm"
              min="1"
              disabled={loading}
            />
            <span className="text-xs text-gray-500">(Optional - reference specific line)</span>
          </div>
          
          <div className="flex gap-2">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={replyTo ? "Write a reply..." : "Add a comment..."}
              className="flex-1 min-h-[80px]"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={loading || !newComment.trim()}
              className="self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
